import type {
  WorkbookCell,
  WorkbookInputOrderRow,
  WorkbookResult,
  WorkbookColumnarSheet,
} from '@erp/shared-types';

/** Which of a Worksheet's filters and sort the output grid applies to the rows it shows. */
export interface AppliedRowSteps {
  filters: boolean;
  sort: boolean;
}

export interface WorksheetGridRow {
  cells: WorkbookCell[];
  /** The index in `filterCounts.filters` of the filter that removed the row, or null when kept. */
  removedBy: number | null;
}

export interface InputOrderView {
  rows: WorksheetGridRow[];
  /** The Input-order preview holds only the first staged rows, so the view is partial. */
  bounded: boolean;
}

const bySortPosition = (a: WorkbookInputOrderRow, b: WorkbookInputOrderRow) =>
  // Rows from Runs before Sort positions existed keep input order.
  (a.sortPosition ?? 0) - (b.sortPosition ?? 0);

/**
 * The rows to show when the filters or the sort are turned off, read from the
 * Input-order preview; undefined when both apply, which is the Result preview.
 */
export function inputOrderView(
  result: WorkbookResult | undefined,
  applied: AppliedRowSteps,
): InputOrderView | undefined {
  const preview = result?.inputOrderRows;
  if (!preview || (applied.filters && applied.sort)) return undefined;
  const kept = applied.filters
    ? preview.filter((row) => row.removedBy === null)
    : preview;
  const ordered = applied.sort ? kept.toSorted(bySortPosition) : kept;
  return {
    rows: ordered.map(({ cells, removedBy }) => ({ cells, removedBy })),
    bounded: (result.filterCounts?.inputRowCount ?? 0) > preview.length,
  };
}

/** Rows shown in place of the Result's preview, each with its place in the Result. */
export interface GridFocus {
  rows: readonly WorkbookCell[][];
  rowNumbers: readonly number[];
}

/**
 * What the output grid shows: the rows a focus names (a Sample taken on
 * request, a Validation's failing rows), else the Input-order view while a
 * step is off, else the Result preview (re-sorted in the browser unless it is
 * a Sample, whose rows keep their places), else placeholder rows. Row numbers
 * are places in the Result when the rows aren't its first ones.
 */
export function outputGridRows(
  result: WorkbookResult | undefined,
  applied: AppliedRowSteps,
  sortPreview: (result: WorkbookResult) => {
    rows: WorkbookCell[][];
    exact: boolean;
  },
  focus: GridFocus | undefined,
): {
  rows: WorksheetGridRow[];
  rowNumbers: readonly number[] | undefined;
  inputOrder: InputOrderView | undefined;
  exact: boolean;
} {
  const kept = (cells: readonly WorkbookCell[][]) =>
    cells.map((row) => ({ cells: row, removedBy: null }));
  if (focus)
    return {
      rows: kept(focus.rows),
      rowNumbers: focus.rowNumbers,
      inputOrder: undefined,
      exact: true,
    };
  const inputOrder = inputOrderView(result, applied);
  if (inputOrder)
    return {
      rows: inputOrder.rows,
      rowNumbers: undefined,
      inputOrder,
      exact: true,
    };
  if (!result)
    return {
      rows: kept(Array.from({ length: 5 }, () => [])),
      rowNumbers: undefined,
      inputOrder,
      exact: true,
    };
  const preview = result.sample
    ? { rows: result.rows, exact: true }
    : sortPreview(result);
  return {
    rows: kept(preview.rows),
    rowNumbers: result.sample?.rowNumbers,
    inputOrder,
    exact: preview.exact,
  };
}

export interface SortKeysLabel {
  /** The leading sort keys, then "+N" for the rest: "Customer ↑, Net ↓ +2". */
  short: string;
  /** Every sort key, for a tooltip. */
  full: string;
}

const sortKey = ({
  column,
  direction,
}: WorkbookColumnarSheet['sort'][number]) =>
  `${column} ${direction === 'asc' ? '↑' : '↓'}`;

/** A Worksheet's sort keys in priority order, with ↑ for ascending and ↓ for descending. */
export function sortKeysLabel(
  sort: WorkbookColumnarSheet['sort'],
  limit = 3,
): SortKeysLabel {
  const full = sort.map(sortKey).join(', ');
  if (sort.length <= limit) return { short: full, full };
  const leading = sort.slice(0, limit).map(sortKey).join(', ');
  return { short: `${leading} +${sort.length - limit}`, full };
}
